import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Check } from "lucide-react";
import { Input } from "../../components/ui/input";
import { OfferYacht } from "./model";

// Shared building blocks for the Convert-to-booking modal — types, constants,
// helpers and small presentational components. Kept out of the modal file so it
// stays focused on flow/state.

export const STEPS = ["Choose yacht", "Upload contract", "Validate & confirm", "Done"] as const;

export const PROVIDER_LABEL: Record<string, string> = {
  NS: "NauSYS",
  BM: "Booking Manager",
  CYA: "CYA",
  Yachtfolio: "Yachtfolio",
  Helm: "Helm",
  Ankor: "Anchor",
};

// Central-agency yachts convert via a signed MYBA/CYBA contract; NauSYS / BM go
// the light option → summary path.
export const needsContract = (provider?: string) => provider === "CYA" || provider === "Yachtfolio";

export const REACTION: Record<string, { cls: string; label: string }> = {
  love: { cls: "bg-green-100 text-green-700", label: "❤ Loved" },
  maybe: { cls: "bg-amber-100 text-amber-700", label: "◐ Maybe" },
  pass: { cls: "bg-slate-100 text-slate-600", label: "✕ Passed" },
};

// Card is free below this (charter currency); above, the broker chooses.
export const CARD_FEE_THRESHOLD = 10000;

export function yachtFee(oy: OfferYacht): number {
  return oy.discountCharterPrice || oy.pricing?.clientPrice || oy.pricing?.totalPrice || 0;
}
export function yachtSpec(oy: OfferYacht): string {
  const y = oy.yacht;
  return [y?.builder || y?.modelName, y?.boatType, y?.maxPeople ? `${y.maxPeople} guests` : null]
    .filter(Boolean)
    .join(" · ");
}

// The public client site the summary page lives on — staging builder → staging
// site, mirroring Preview.tsx.
export function publicHost(brand?: string): string {
  const staging = typeof window !== "undefined" && window.location.hostname.includes("staging");
  if (brand === "anchor") return staging ? "https://staging.anchor.yt" : "https://www.anchor.yt";
  return staging ? "https://staging.helm.yt" : "https://www.helm.yt";
}

// The raw parser envelope (mirrors api/contracts/types.ts; kept local to avoid a
// cross-package import into the web build).
export type ParsedParty = { name: string | null; address: string | null; vatNumber?: string | null };
export type ParseResult = {
  ok: boolean;
  template: "myba" | "cyba" | "unknown";
  parsed: {
    template: "myba" | "cyba" | "unknown";
    financials: {
      charterFee: number | null;
      vat: number | null;
      vatLabel: string | null;
      apa: number | null;
      delivery: number | null;
      additionalFees: number | null;
      total: number | null;
      currency: string | null;
      paymentSchedule?: { sequence?: number | null; amount: number | null; dueDate?: string | null; label?: string | null }[];
    };
    owner: ParsedParty | null;
    stakeholder: ParsedParty | null;
    yacht: { name: string | null; type: string | null; flag: string | null; length: string | null } | null;
  };
  netCharterFee: number | null;
  error?: { kind: string; message: string };
};

// The validate-step form — editable, seeded from the parse result + offer.
export type Party = { name: string; address: string; vatNumber: string; commissionInvoiceContact?: string };
export type ConvertForm = {
  template: "myba" | "cyba" | "unknown";
  filename: string;
  yacht: { name: string; type: string; flag: string; length: string };
  dates: { startDate: string; endDate: string; embarkTime: string; disembarkTime: string; startLocation: string; endLocation: string };
  financials: { charterFee: number; vat: number; vatLabel: string; apa: number; delivery: number; additionalFees: number; total: number; currency: string };
  billing: { firstName: string; lastName: string; email: string; phone: string; street: string; city: string; postalCode: string; country: string };
  schedule: { label: string; amount: number; dueDate: string }[];
  stakeholder: Party;
  owner: Party;
};

export function num(v: unknown): number {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
}

export function Badge({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span className={clsx("inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium", className)}>
      {children}
    </span>
  );
}

// A labelled edit row used across the validate groups.
export function Row({
  label,
  hint,
  children,
  required,
  filled,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  required?: boolean;
  filled?: boolean;
}) {
  return (
    <div className="grid grid-cols-[30%_1fr_auto] items-center gap-3 py-1.5">
      <div className="text-sm text-slate-500">
        {label}
        {hint && <div className="text-xs text-slate-400">{hint}</div>}
      </div>
      <div>{children}</div>
      <div className="text-right">
        {required && !filled ? (
          <Badge className="bg-red-100 text-red-600">required</Badge>
        ) : (
          <Badge className="bg-green-100 text-green-700">
            <Check size={11} /> ok
          </Badge>
        )}
      </div>
    </div>
  );
}

export function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-sm font-semibold text-slate-700">{title}</div>
      <div className="rounded-lg border border-blue-100 p-4">{children}</div>
    </div>
  );
}

/**
 * A money input that keeps the exact string the broker is typing (so "1250.50"
 * and a trailing "." survive re-renders) while reporting a parsed number. Without
 * this, binding `value={String(number)}` strips the decimal on every keystroke.
 */
export function MoneyInput({
  value,
  onChange,
  ...rest
}: { value: number; onChange: (n: number) => void } & Omit<
  React.ComponentProps<typeof Input>,
  "value" | "onChange"
>) {
  const [raw, setRaw] = useState(value ? String(value) : "");
  const last = useRef(value);
  // Resync when the value changes from outside (parse seed / reset) — but not
  // from our own onChange, so mid-edit strings aren't clobbered.
  useEffect(() => {
    if (value !== last.current) {
      setRaw(value ? String(value) : "");
      last.current = value;
    }
  }, [value]);
  return (
    <Input
      {...rest}
      inputMode="decimal"
      value={raw}
      onChange={(e) => {
        const v = e.target.value;
        setRaw(v);
        const parsed = parseFloat(v.replace(/[^0-9.]/g, ""));
        const n = isNaN(parsed) ? 0 : parsed;
        last.current = n;
        onChange(n);
      }}
    />
  );
}
